import { cloneDeep, isEqual } from "lodash";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useRequest } from "redux-query-react";
import { useGetCustomDatapointDimensions } from "../use-datapoints";
import { setupBenchmarkIdContext } from "./benchmark-id-patcher";
import { filterCffModelMeasures, setupCffModelIdContext } from "./cff-model-id-patcher";
import { filterCffHistoricalMeasures, filterCffHistoricalLabels } from "./cff-historical-patcher";
import { filterFundLevelAxesForPacing } from "./pacing-select-values-patcher";
import { setupCffModalFilters } from "./cff-modal-filter-patcher";
import { setupContextObjective } from "./context-patcher";
import { addCustomFieldsToGridProps, addCustomFieldsToSelValues } from "./custom-fields-patchers";
import { addSelectedGroupByColumns, moveSelectedGroupByColumns, replaceDimensionsWithSelectedGroupByColumns } from "./group-by-patchers";
import { setupGenericIndicesContext, setupPublicIndicesContext } from "./pmi-patcher";
import { PmiFromJSON, getGenericPmis, useGetPmiAssociations, useGetPmis } from "api/admin/src";
import { DatapointsEntityType, useGetDatapointsMetadata } from "api/ux/src";
import { getDatapointGridStateId } from "components/widgets/grid/common";
import { useCashflowsCustomFields } from "components/widgets/grid/hooks/use-cashflows-custom-fields";
import { useWidgetComponentProps, useWidgetCurrentGridSwitch } from "components/widgets/hooks/use-current-widget";
import { useCurrentDashboardEntityId, useCurrentDashboardType, useIncludeHistoricalCashflows } from "components/workspaces/hooks/use-current-dashboard";
import { CommmonFormatEnum } from "datapoints";
import { dashboardTypeToEntityType } from "hooks/shared/dashboardTypeToEntityType";
import { componentPropsPatcher } from "store/current-dashboard/types";
import { useCffChangeModal } from "store/current-dashboard/selectors";
import { useGetGridGroups } from "store/grids";
import { removePreqinDefaultGroup } from "./default-group-patchers";
const useGetPmiAssociationsForEntity = (entityId, entityType) => {
    const [associations] = useGetPmiAssociations({ entityId, entityType });
    return associations.filter((a) => a.entityId === entityId);
};
const useGetGenericPmis = () => {
    useRequest(getGenericPmis({
        transform: (body) => {
            const objResponse = body.map(PmiFromJSON);
            return {
                GenericPmis: objResponse.reduce((map, obj) => {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    map[obj.benchmarkId] = obj;
                    return map;
                }, {})
            };
        },
        update: {
            GenericPmis: (prevValue, transformedValue) => ({ ...prevValue, ...transformedValue })
        }
    }));
    return useSelector((s) => s.entities.GenericPmis, isEqual);
};
const useGetSelectedPmis = () => {
    const entityType = dashboardTypeToEntityType(useCurrentDashboardType());
    const entityId = useCurrentDashboardEntityId();
    const [indices] = useGetPmis();
    const associations = useGetPmiAssociationsForEntity(entityId, entityType);
    return associations.flatMap((a) => a.pmis)
        .map((p) => ({ id: p ?? "", label: indices?.find((m) => m.benchmarkId === p)?.name ?? "" }));
};
const useAreAllFormatsValid = (standardDatapoint) => {
    const [datapointsMetadata] = useGetDatapointsMetadata();
    const isValid = useMemo(() => {
        const allowedFormats = [CommmonFormatEnum.string, CommmonFormatEnum.year, CommmonFormatEnum.entity];
        if (!standardDatapoint || standardDatapoint.length === 0 || !datapointsMetadata) {
            return false;
        }
        return standardDatapoint.every((sd) => {
            const metadata = datapointsMetadata.find((meta) => meta.id === sd);
            const format = metadata?.metadata?.format;
            return format !== undefined && allowedFormats.includes(format);
        });
    }, [standardDatapoint, datapointsMetadata]);
    return isValid;
};
// TO BE DONE call dependencies only when needed
const useGetComponentPropsPatchersDependencies = (sectionId, widgetId) => {
    const hardcodedPmis = useWidgetComponentProps(sectionId, widgetId)?.pmisOverrides;
    const selectedPmis = useGetSelectedPmis();
    const id = useCurrentDashboardEntityId();
    const type = useCurrentDashboardType();
    const otherModulesData = useSelector((state) => state?.otherModulesData, isEqual);
    const cffChangeModal = useCffChangeModal();
    const cffModelId = cffChangeModal?.selectedModelId;
    const changeBenchmarkModal = useSelector((state) => state.currentDashboard.changeBenchmarkModal);
    const changeBenchmarkBenchmarkId = changeBenchmarkModal?.benchmarkId;
    const changeBenchmarkFundId = changeBenchmarkModal?.fundId;
    const selectedBenchmarkId = changeBenchmarkModal?.selectedBenchmarkId;
    const changeBenchmarkInvestorId = changeBenchmarkModal?.investorId;
    const changeBenchmarkInvestmentId = changeBenchmarkModal?.investmentId;
    return {
        cffChangeModal,
        cffModelId,
        changeBenchmarkBenchmarkId,
        changeBenchmarkFundId,
        changeBenchmarkInvestmentId,
        changeBenchmarkInvestorId,
        customDimensions: {
            [DatapointsEntityType.Asset]: useGetCustomDatapointDimensions([DatapointsEntityType.Asset]),
            [DatapointsEntityType.Fund]: useGetCustomDatapointDimensions([DatapointsEntityType.Fund]),
            InvestorAccount: useGetCustomDatapointDimensions(["Investor Account"])
        },
        genericIndices: useGetGenericPmis(),
        includeHistoricalCashflows: useIncludeHistoricalCashflows(sectionId || ""),
        objective: otherModulesData?.[`${type}/${id}/objective`],
        pacingPlanObjective: otherModulesData?.[`${type}/${id}/pacingPlanObjective`],
        pmiPreferences: hardcodedPmis ?? selectedPmis,
        selectedBenchmarkId,
        switched: useWidgetCurrentGridSwitch(sectionId, widgetId),
        userSelectedGroups: useGetGridGroups(getDatapointGridStateId(sectionId || "", widgetId || ""), useWidgetComponentProps(sectionId, widgetId)?.columnsTemplate?.columns),
        widgetId
    };
};
const ComponentPropsPatchers = {
    [componentPropsPatcher.addCustomFieldsToGridProps]: addCustomFieldsToGridProps,
    [componentPropsPatcher.addCustomFieldsToSelValues]: addCustomFieldsToSelValues,
    [componentPropsPatcher.moveSelectedGroupByColumns]: moveSelectedGroupByColumns,
    [componentPropsPatcher.addSelectedGroupByColumns]: addSelectedGroupByColumns,
    [componentPropsPatcher.replaceDimensionsWithSelectedGroupByColumns]: replaceDimensionsWithSelectedGroupByColumns,
    [componentPropsPatcher.setupPublicIndicesContext]: setupPublicIndicesContext,
    [componentPropsPatcher.setupGenericIndicesContext]: setupGenericIndicesContext,
    [componentPropsPatcher.setupContextObjective]: setupContextObjective,
    [componentPropsPatcher.removePreqinDefaultGroup]: removePreqinDefaultGroup,
    [componentPropsPatcher.setupCffModelIdContext]: setupCffModelIdContext,
    [componentPropsPatcher.filterCffModelMeasures]: filterCffModelMeasures,
    [componentPropsPatcher.setupCffModalFilters]: setupCffModalFilters,
    [componentPropsPatcher.setupBenchmarkIdContext]: setupBenchmarkIdContext,
    [componentPropsPatcher.filterCffHistoricalMeasures]: filterCffHistoricalMeasures,
    [componentPropsPatcher.filterCffHistoricalLabels]: filterCffHistoricalLabels,
    [componentPropsPatcher.filterFundLevelAxesForPacing]: filterFundLevelAxesForPacing,
};
export const usePatchComponentProps = (originalComponentProps, sectionId, widgetId) => {
    const allDependencies = useGetComponentPropsPatchersDependencies(sectionId, widgetId);
    const standardDatapointsMeasures = originalComponentProps.datapointParams?.flatMap((param) => param.measures?.map((measure) => measure.standardDatapoint));
    const allFormatsAreValidMeasures = useAreAllFormatsValid(standardDatapointsMeasures || []);
    let componentProps = cloneDeep(originalComponentProps);
    // Remove "moveSelectedGroupByColumns" patcher if all formats are valid
    if (allFormatsAreValidMeasures) {
        componentProps.componentPropsPatchers = componentProps.componentPropsPatchers?.filter((patcherObj) => patcherObj.patcher !== "moveSelectedGroupByColumns");
    }
    const patcher = allDependencies.switched
        ? componentProps?.gridSwitcher?.componentPropsPatchers ||
            componentProps?.componentPropsPatchers
        : componentProps?.componentPropsPatchers;
    patcher
        ?.filter((p) => ComponentPropsPatchers[p.patcher])
        .forEach((p) => {
        componentProps = ComponentPropsPatchers[p.patcher](componentProps, allDependencies, originalComponentProps, p.argument);
    });
    // Apply cashflows custom fields enhancement for grid widgets
    componentProps = useCashflowsCustomFields(widgetId || "", componentProps);
    return componentProps;
};
